import React, { useEffect, useMemo, useState } from 'react';

import { sameOriginApiBase } from '../../util/sameOriginApiBase';
import { createSlug } from '../../util/urlHelpers';

const lookupCache = new Map();

export const venueDisplayNameFromPublicData = publicData =>
  publicData?.venueName || publicData?.venue || null;

const venueLookupParams = publicData => {
  const venueName = venueDisplayNameFromPublicData(publicData);
  const venueId = publicData?.ticketNetworkVenueId;
  if (!venueName && !venueId) {
    return null;
  }
  return {
    ...(venueId ? { venueId: String(venueId) } : {}),
    ...(venueName ? { name: venueName, slug: createSlug(venueName) } : {}),
  };
};

const SeoVenueLinkMaybe = ({ publicData, className, children }) => {
  const fallbackText = children || venueDisplayNameFromPublicData(publicData);
  const params = useMemo(() => venueLookupParams(publicData), [publicData]);
  const cacheKey = params ? JSON.stringify(params) : null;
  const [path, setPath] = useState(() => (cacheKey ? lookupCache.get(cacheKey) || null : null));

  useEffect(() => {
    if (!cacheKey || lookupCache.has(cacheKey) || typeof fetch !== 'function') {
      return;
    }
    const base = sameOriginApiBase();
    if (!base) {
      return;
    }

    let cancelled = false;
    const query = new URLSearchParams(params).toString();
    fetch(`${base}/api/seo-discovery/venue-link?${query}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(res => (res.ok ? res.json() : { found: false }))
      .then(body => {
        const nextPath = body?.found ? body.path : null;
        lookupCache.set(cacheKey, nextPath);
        if (!cancelled) {
          setPath(nextPath);
        }
      })
      .catch(() => {
        lookupCache.set(cacheKey, null);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, params]);

  if (!fallbackText) {
    return null;
  }

  return path ? (
    <a className={className} href={path}>
      {fallbackText}
    </a>
  ) : (
    <span className={className}>{fallbackText}</span>
  );
};

export default SeoVenueLinkMaybe;
